import { FC } from 'react';
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { IDiagram } from 'entities/statistics/types/statistics-interface';

export interface BarDataItem {
  name: string;
  value: number;
}

interface BarChartComponentProps {
  data: IDiagram[] | BarDataItem[];
  width: number;
  height: number;
  title: string;
  layout?: 'horizontal' | 'vertical';
  barFill?: string;
  hideSmallItems?: boolean;
  isAnimationActive?: boolean;
  labelFontSize?: number;
  tickMargin?: number;
}

export const BarChartComponent: FC<BarChartComponentProps> = ({
  data,
  width,
  height,
  title,
  layout = 'vertical',
  barFill = '#0054E6',
  hideSmallItems = true,
  isAnimationActive = true,
  labelFontSize = 10,
  tickMargin = 5,
}) => {
  const transformedData: BarDataItem[] = data.map((item, index) => ({
    name: 'Field' in item ? item.Field : item.name,
    value: 'Amount' in item ? item.Amount || 0 : item.value,
    index,
  }));

  const totalValue = transformedData.reduce((sum, item) => sum + item.value, 0);
  const maxValue = transformedData.length > 0
    ? Math.ceil(Math.max(...transformedData.map(item => item.value)))
    : 100;

  const renderCustomXAxisTick = (props: any) => {
    const { x, y, payload, index } = props;
    const offset = index % 2 === 0 ? 0 : 10;

    return (
      <g transform={`translate(${x}, ${y + offset})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#666"
          fontSize={labelFontSize}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <>
      <p style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'center' }}>
        {title}
      </p>
      <BarChart
        layout={layout}
        width={width}
        height={height}
        data={transformedData}
        margin={{
          top: 20,
          right: 30,
          left: layout === 'vertical' ? 0 : 20,
          bottom: layout === 'horizontal' ? 50 : 5,
        }}
      >
        <CartesianGrid strokeDasharray="2 3" />
        {layout === 'vertical' ? (
          <>
            <XAxis
              type="number"
              domain={[0, maxValue]}
            />
            <YAxis
              dataKey="name"
              type="category"
              tick={{ fontSize: labelFontSize }}
              tickMargin={tickMargin}
              width={150}
              minTickGap={0}
              interval={0}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="name"
              tick={renderCustomXAxisTick}
              tickMargin={tickMargin}
              interval={0}
            />
            <YAxis
              domain={[0, maxValue]}
              tick={{ fontSize: labelFontSize }}
              tickMargin={tickMargin}
            />
          </>
        )}
        <Tooltip
          formatter={(value) => [`${value}`, 'Значение']}
        />
        <Bar
          dataKey="value"
          fill={barFill}
          isAnimationActive={isAnimationActive}
        />
        {hideSmallItems && (
          <Legend
            content={({ payload }) => {
              if (!payload) return null;
              return (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {payload.map((entry, index) => {
                    const item = transformedData[index];
                    if (!item) return null;
                    const percent = (item.value / totalValue) * 100;
                    if (percent < 5) return null;
                    return (
                      <li
                        key={`item-${index}`}
                        style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
                      >
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: barFill,
                            marginRight: '8px',
                          }}
                        />
                        <span style={{ fontSize: '12px' }}>{item.name}</span>
                      </li>
                    );
                  })}
                </ul>
              );
            }}
          />
        )}
      </BarChart>
    </>
  );
};